import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { LoginResponseDto, Problem } from '@fms/contracts';
import { isSealedPayload, PayloadCipher, PAYLOAD_KID_HEADER } from './payload-crypto';

/**
 * The single axios instance for a portal. Access token lives in memory only
 * (never localStorage — XSS can't lift it); the refresh token is an httpOnly
 * cookie the browser attaches to /auth/refresh automatically. A 401 triggers
 * one single-flight refresh; concurrent 401s await the same promise.
 */
export interface ApiClientOptions {
  baseURL: string;
  /**
   * Which portal this client serves. Citizen and staff sessions use separate
   * refresh/CSRF cookie pairs on the API host; this scopes every auth request
   * to the right pair so the two logins are fully independent.
   */
  portal?: 'citizen' | 'staff';
  /** Emitted when the session is unrecoverable (refresh failed / reuse detected). */
  onAuthLost: () => void;
  /** Broadcast fresh CSRF + token after login/refresh so all tabs stay in sync. */
  onTokenChanged?: (accessToken: string | null) => void;
  /**
   * Transport payload encryption (see docs/lld/auth-security.md).
   * 'auto' (default): start in plaintext and transparently upgrade to sealed
   * transport when the API answers 428 PAYLOAD_KEY_REQUIRED — so one build
   * works against both local (plain) and production (sealed) APIs.
   * true: seal from the very first request. false: never seal.
   */
  payloadEncryption?: boolean | 'auto';
}

const LEGACY_CSRF_COOKIE = 'fms_csrf';
const PORTAL_HEADER = 'x-fms-portal';

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

type RetriableConfig = InternalAxiosRequestConfig & {
  _retried?: boolean;
  _pcRetried?: boolean;
  /** Pre-seal body kept for transparent retries after a key rollover. */
  _pcPlainData?: unknown;
};

export class ApiClient {
  readonly axios: AxiosInstance;
  private accessToken: string | null = null;
  private refreshPromise: Promise<string | null> | null = null;
  private readonly cipher: PayloadCipher;
  private readonly payloadMode: boolean | 'auto';
  private readonly tokenListeners = new Set<(token: string | null) => void>();

  constructor(private readonly options: ApiClientOptions) {
    this.axios = axios.create({
      baseURL: options.baseURL,
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30_000,
    });
    this.payloadMode = options.payloadEncryption ?? 'auto';
    this.cipher = new PayloadCipher(this.axios, this.payloadMode === true);

    this.axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      if (this.accessToken) config.headers.set('Authorization', `Bearer ${this.accessToken}`);
      // Scope cookie-credentialed auth calls to this portal's session pair.
      if (options.portal) config.headers.set(PORTAL_HEADER, options.portal);
      // Double-submit CSRF for the cookie-credentialed auth mutations.
      if (config.url?.includes('/auth/') && config.method !== 'get') {
        const csrf =
          (options.portal ? readCookie(`fms_csrf_${options.portal}`) : null) ??
          readCookie(LEGACY_CSRF_COOKIE);
        if (csrf) config.headers.set('x-csrf-token', csrf);
      }
      // Multipart/binary bodies carry no JSON to seal, and letting the browser
      // set Content-Type is what produces the multipart boundary. The kid
      // header is still sent below so the request satisfies encrypted-transport
      // deployments.
      const isBinaryBody =
        (typeof FormData !== 'undefined' && config.data instanceof FormData) ||
        (typeof Blob !== 'undefined' && config.data instanceof Blob) ||
        config.data instanceof ArrayBuffer;
      if (isBinaryBody) config.headers.delete('Content-Type');

      // Sealed transport — everything except the handshake bootstrap itself.
      if (this.cipher.active && !config.url?.includes('/crypto/handshake')) {
        const kid = await this.cipher.ensureKey();
        config.headers.set(PAYLOAD_KID_HEADER, kid);
        if (!isBinaryBody && config.data !== undefined && config.data !== null) {
          const plain =
            typeof config.data === 'string' ? (JSON.parse(config.data) as unknown) : config.data;
          if (!isSealedPayload(plain)) {
            (config as RetriableConfig)._pcPlainData = plain;
            config.data = await this.cipher.seal(plain);
          } else {
            config.data = plain;
          }
        }
      }
      return config;
    });

    this.axios.interceptors.response.use(
      async (response) => {
        if (isSealedPayload(response.data)) {
          response.data = await this.cipher.unseal(response.data);
        }
        return response;
      },
      async (error: AxiosError<Problem>) => {
        // Unseal problem bodies first so the taxonomy checks below see them.
        if (error.response && isSealedPayload(error.response.data)) {
          try {
            error.response.data = (await this.cipher.unseal(error.response.data)) as Problem;
          } catch {
            /* leave sealed — treated as an opaque error below */
          }
        }
        const original = error.config as RetriableConfig | undefined;
        const status = error.response?.status;
        const code = error.response?.data?.code;

        // The API demands sealed transport (production): handshake, re-seal
        // the original body with the fresh key and replay once.
        if (
          status === 428 &&
          code === 'PAYLOAD_KEY_REQUIRED' &&
          this.payloadMode !== false &&
          original &&
          !original._pcRetried
        ) {
          original._pcRetried = true;
          this.cipher.activate();
          this.cipher.invalidate();
          const retryData =
            original._pcPlainData ??
            (typeof original.data === 'string'
              ? (JSON.parse(original.data) as unknown)
              : original.data);
          return this.axios({ ...original, data: retryData });
        }

        // One refresh attempt on a genuine token expiry, never on the refresh call itself.
        if (
          status === 401 &&
          code === 'AUTH_TOKEN_EXPIRED' &&
          original &&
          !original._retried &&
          !original.url?.includes('/auth/refresh')
        ) {
          original._retried = true;
          const token = await this.refresh();
          if (token) {
            original.headers.set('Authorization', `Bearer ${token}`);
            return this.axios(original);
          }
        }
        if (
          status === 401 &&
          (code === 'AUTH_SESSION_REVOKED' || code === 'AUTH_TOKEN_REUSE_DETECTED')
        ) {
          this.setAccessToken(null);
          this.options.onAuthLost();
        }
        return Promise.reject(error);
      },
    );
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
    this.options.onTokenChanged?.(token);
    for (const listener of this.tokenListeners) listener(token);
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Reactive access-token subscription (login, refresh, logout, auth-lost).
   * Session providers use it so the UI flips signed-in state immediately —
   * without waiting for a query cache round-trip. Returns an unsubscribe.
   */
  subscribeToken(listener: (token: string | null) => void): () => void {
    this.tokenListeners.add(listener);
    return () => this.tokenListeners.delete(listener);
  }

  /**
   * Single-flight refresh — concurrent 401s share one network call.
   * `notifyOnFailure` is false for the boot-time probe: an anonymous visitor
   * legitimately has no refresh cookie, and that must NOT be treated as a lost
   * session (doing so bounces first-time visitors off public pages).
   */
  private refresh(notifyOnFailure = true): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.axios
      .post<LoginResponseDto>('/auth/refresh')
      .then((res) => {
        this.setAccessToken(res.data.accessToken);
        return res.data.accessToken;
      })
      .catch(() => {
        this.setAccessToken(null);
        if (notifyOnFailure) this.options.onAuthLost();
        return null;
      })
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  /**
   * Optional silent session restore on app boot. Failure is expected and
   * silent — route guards decide what an anonymous visitor may see.
   */
  async bootstrap(): Promise<string | null> {
    return this.refresh(false);
  }
}

/** Normalize any axios error into the RFC-7807 problem shape for UI handling. */
export function toProblem(error: unknown): Problem {
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    typeof error.response.data === 'object' &&
    !isSealedPayload(error.response.data)
  ) {
    return error.response.data as Problem;
  }
  return {
    type: 'about:blank',
    title: 'Network error',
    status: 0,
    code: 'INTERNAL',
    detail: error instanceof Error ? error.message : 'Request failed',
  };
}
